import { useState } from 'react';
import { Button, Modal, Radio, Tag, Typography } from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import {
	PRODUCTION_METHOD_SCOPES,
	ProductionMethodScope,
} from '../../../hooks/usePersistedProductionMethod';
import { TABLET_FILTER_BUTTON_STYLE } from '../tabletStyles';

const SCOPE_LABELS: Record<ProductionMethodScope, string> = {
	All: 'All methods',
	Fastlane: 'Fastlane',
	Swiss: 'Swiss',
};

const SCOPE_HELP: Record<ProductionMethodScope, string> = {
	All: 'Every cut, whichever method it is for. Use this if this saw serves both.',
	Fastlane: 'Only cuts for Fastlane jobs.',
	Swiss: 'Only cuts for Swiss jobs.',
};

export type ProductionMethodCopy = {
	title: string;
	description: string;
	help: Record<ProductionMethodScope, string>;
	testId?: string;
};

export const SAW_PRODUCTION_METHOD_COPY: ProductionMethodCopy = {
	title: 'What this saw cuts',
	description:
		'Set this once for the station. The queue stays on this method until someone changes it here, on this tablet.',
	help: SCOPE_HELP,
	testId: 'fastlane-cut-production-method',
};

export const KITTING_PRODUCTION_METHOD_COPY: ProductionMethodCopy = {
	title: 'What this station kits',
	description:
		'Set this once for the station. The queue stays on this method until someone changes it here, on this tablet.',
	help: {
		All: 'Every kit, whichever method it is for. Use this if this station kits both.',
		Fastlane: 'Only kits for Fastlane jobs.',
		Swiss: 'Only kits for Swiss jobs.',
	},
	testId: 'fastlane-kit-production-method',
};

interface Props {
	scope: ProductionMethodScope;
	onChange: (scope: ProductionMethodScope) => void;
	copy?: ProductionMethodCopy;
}

/**
 * The station's production method, shown as what it is rather than as a choice
 * to make: one tag naming the current setting, where three buttons used to sit.
 *
 * Changing it goes through a modal with its own Confirm, the way the building
 * badge does. A single tap would be quicker, but it would also make the setting
 * easier to change by accident than the filter it replaced, and a station
 * quietly switched to the wrong method shows an operator a queue that is missing
 * work without saying so.
 */
export default function ProductionMethodIndicator({
	scope,
	onChange,
	copy = SAW_PRODUCTION_METHOD_COPY,
}: Props) {
	const [modalOpen, setModalOpen] = useState(false);
	const [selected, setSelected] = useState<ProductionMethodScope>(scope);
	const testId = copy.testId ?? 'fastlane-cut-production-method';

	return (
		<>
			<Tag
				data-testid={testId}
				data-production-method={scope}
				icon={<ToolOutlined />}
				color={scope === 'All' ? 'default' : 'blue'}
				style={{
					fontSize: 16,
					fontWeight: 'normal',
					padding: '8px 14px',
					margin: 0,
					cursor: 'pointer',
					lineHeight: 1.4,
				}}
				onClick={() => {
					setSelected(scope);
					setModalOpen(true);
				}}
			>
				{SCOPE_LABELS[scope]}
			</Tag>

			<Modal
				open={modalOpen}
				title={copy.title}
				onCancel={() => setModalOpen(false)}
				footer={null}
				centered
				width={460}
			>
				<Typography.Paragraph type="secondary">
					{copy.description}
				</Typography.Paragraph>
				<Radio.Group
					value={selected}
					onChange={(e) => setSelected(e.target.value)}
					style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
				>
					{PRODUCTION_METHOD_SCOPES.map((option) => (
						<Radio
							key={option}
							value={option}
							data-testid={`${testId}-${option}`}
							style={{ fontSize: 16, alignItems: 'center' }}
						>
							<strong>{SCOPE_LABELS[option]}</strong>
							<div style={{ fontSize: 14, color: '#8c8c8c' }}>
								{copy.help[option]}
							</div>
						</Radio>
					))}
				</Radio.Group>
				<Button
					type="primary"
					size="large"
					block
					style={{ ...TABLET_FILTER_BUTTON_STYLE, marginTop: 20 }}
					onClick={() => {
						onChange(selected);
						setModalOpen(false);
					}}
				>
					Confirm
				</Button>
			</Modal>
		</>
	);
}
